import React from "react";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { axiosPrivate } from "@/api/axios";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { uploadFileToFirebase } from "@/lib/firebase/mediaUploading";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MediaFormField from "@/components/ui/mediaFormField";
import { CountryDropdown } from "react-country-region-selector";

const formSchema = z.object({
  country: z.string().min(1, "Country is required"),
  name: z.string().optional(),
  person: z.string().min(1, "Person is required"),
  position: z.string().optional(),
  phone1: z.string().min(1, "Phone Number 1 is required"),
  phone2: z.string().optional(),
  whatsapp1: z.string().optional(),
  whatsapp2: z.string().optional(),
  email1: z
    .string({ required_error: "Email 1 is required" })
    .min(1, "Email 1 is required")
    .email("Invalid email"),
  // email2 is optional; allow empty string without triggering validation
  email2: z
    .union([z.string().email("Invalid email"), z.literal("")])
    .optional(),
  website: z.string().optional(),
  address_line1: z.string().min(1, "Address Line 1 is required"),
  address_line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  province_state: z.string().optional(),
  price_list: z
    .string({ required_error: "Price list is required" })
    .min(1, "Price list is required")
    .url("Invalid URL"),
});

function AddDirectory() {
  const queryClient = useQueryClient();
  const form = useForm({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues: {
      country: "",
      name: "",
      person: "",
      position: "",
      phone1: "",
      phone2: "",
      whatsapp1: "",
      whatsapp2: "",
      email1: "",
      email2: "",
      website: "",
      address_line1: "",
      address_line2: "",
      city: "",
      province_state: "",
      price_list: "",
    },
  });
  const [priceListUrl, setPriceListUrl] = React.useState("");
  const navigate = useNavigate();

  const createEntryMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      // Clean payload: trim strings and drop blanks
      const cleaned: Record<string, any> = {};
      Object.entries(data).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (typeof v === "string") {
          const t = v.trim();
          if (t === "") return;
          cleaned[k] = t;
        } else {
          cleaned[k] = v;
        }
      });
      const res = await axiosPrivate.post("/dxn-directory/", cleaned);
      if (!res.data.success) {
        throw new Error("Failed to add directory entry");
      }
      return res.data;
    },
    onSuccess: () => {
      toast.success("Directory entry added");
      form.reset();
      setPriceListUrl("");
      queryClient.invalidateQueries({ queryKey: ["directories"] });
      navigate("/dashboard/dxn-office-directory/manage-office-directory");
    },
    onError: (error: any) => {
      if (error?.response?.data) {
        // Display specific validation errors if available
        const validationErrors = error.response.data.data;
        if (validationErrors && typeof validationErrors === 'object') {
          const errorMessages = Object.entries(validationErrors).map(
            ([field, messages]: [string, any]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
          ).join('\n');
          toast.error(`Validation errors:\n${errorMessages}`);
        } else {
          toast.error(`Failed to add entry: ${error.response.data.message || 'Unknown error'}`);
        }
      } else {
        toast.error(error.message || "Failed to add entry");
      }
    },
  });

  async function handleSubmit(data: z.infer<typeof formSchema>) {
    createEntryMutation.mutate(data);
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl">Add Directory Entry</h1>
      <Form {...form}>
        <form className="space-y-8" onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Directory Details</h3>
            <div className="grid grid-cols-2 gap-5">
              <FormField name="country" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>
                    Country <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <CountryDropdown
                      value={field.value}
                      onChange={field.onChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="name" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Company Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="person" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>
                    Person <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Person" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="position" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Position</FormLabel>
                  <FormControl>
                    <Input placeholder="Position" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="phone1" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>
                    Phone 1 <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Phone 1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="phone2" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Phone 2</FormLabel>
                  <FormControl>
                    <Input placeholder="Phone 2" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="whatsapp1" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>WhatsApp 1</FormLabel>
                  <FormControl>
                    <Input placeholder="WhatsApp 1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="whatsapp2" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>WhatsApp 2</FormLabel>
                  <FormControl>
                    <Input placeholder="WhatsApp 2" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="email1" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>
                    Email 1 <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="email1@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="email2" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Email 2</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="email2@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="website" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              {/* Spacer to keep Address Line 1 & 2 on the next row together while Website stays single-column */}
              <div aria-hidden className="col-span-1" />
              <FormField name="address_line1" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>
                    Address Line 1 <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Address Line 1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="address_line2" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Address Line 2</FormLabel>
                  <FormControl>
                    <Input placeholder="Address Line 2" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="city" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>
                    City <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="City" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="province_state" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Province/State</FormLabel>
                  <FormControl>
                    <Input placeholder="Province/State" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="price_list" control={form.control} render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>
                    Price List (Link) <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} onChange={(e) => { field.onChange(e.target.value); setPriceListUrl(e.target.value); }} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
          </div>
          
          {/* Optional: upload a hosted Price List and capture URL */}
          {/* Keeping an upload control commented; can enable later if API expects file hosting */}

          <Button type="submit" disabled={!form.formState.isValid}>
            Add Entry
          </Button>
        </form>
      </Form>
    </div>
  );
}

export default AddDirectory;

// function ImageField({
//   image,
//   setImage,
//   setImageUrl,
// }: {
//   image: File | null;
//   setImage: React.Dispatch<React.SetStateAction<File | null>>;
//   setImageUrl: React.Dispatch<React.SetStateAction<string>>;
// }) {
//   const [isUploading, setIsUploading] = React.useState(false),
//     [isUploadSuccess, setIsUploadSuccess] = React.useState(false),
//     [isOpen, setIsOpen] = React.useState(false);

//   async function handleImageUpload() {
//     try {
//       if (!image) return;
//       setIsUploading(true);
//       const imageUrl = await uploadFileToFirebase(image);
//       setImageUrl(imageUrl);
//       setIsUploading(false);
//       setIsUploadSuccess(true);
//       toast.success("Image uploaded successfully");
//
//     } catch (error) {
//
//       toast.error("Failed to upload image");
//     } finally {
//       setIsUploading(false);
//     }
//   }

//   return (
//     <>
//       <Collapsible open={isOpen} onOpenChange={setIsOpen}>
//         <div className="flex items-center gap-1">
//           <CollapsibleTrigger asChild>
//             <Button
//               variant={"ghost"}
//               size={"icon"}
//               className={`size-8 hover:bg-green-300 ${image ? "" : "hidden"}`}
//             >
//               <ChevronsUpDown />
//             </Button>
//           </CollapsibleTrigger>
//           <Input
//             type="file"
//             accept=".png, .jpeg, .jpg"
//             onChange={(e) => {
//               const file = e.target.files?.[0];
//               setImage(file || null);
//               setIsOpen(true);
//             }}
//           />
//           <Button
//             type="button"
//             disabled={isUploading || image == null || isUploadSuccess}
//             className="ml-10"
//             onClick={handleImageUpload}
//           >
//             {isUploading
//               ? "Uploading Image"
//               : isUploadSuccess
//               ? "Uploaded Successfully"
//               : "Upload Image"}
//           </Button>
//         </div>
//         <CollapsibleContent>
//           <div className="mt-4">
//             <img
//               src={image ? URL.createObjectURL(image) : undefined}
//               className="w-full h-full object-contain rounded-md"
//             />
//           </div>
//         </CollapsibleContent>
//       </Collapsible>
//     </>
//   );
// }
